import React, { useState, useEffect, useMemo } from "react";
import { Drawer, Typography, Tag, Divider, Tabs, Empty } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { UserProfile } from "../../../../Entities/UserProfile";
import "./ResumePreviewDrawer.scss";

const { Text, Title } = Typography;

// Keyframes for floating animation
const floatingKeyframes = `
  @keyframes aiFloating {
    0% { transform: translateY(0px); }
    25% { transform: translateY(-8px); }
    50% { transform: translateY(-12px); }
    75% { transform: translateY(-8px); }
    100% { transform: translateY(0px); }
  }
  @keyframes textPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
`;

interface ResumePreviewDrawerProps {
  setIsResumeViewing: React.Dispatch<
    React.SetStateAction<{
      isResumeViewing: boolean;
      resumeUrl: string | null;
    }>
  >;
  isResumeViewing: {
    isResumeViewing: boolean;
    resumeUrl: string | null;
  };
  userProfile?: UserProfile | null;
}
const ResumePreviewDrawer: React.FC<ResumePreviewDrawerProps> = ({
  setIsResumeViewing,
  isResumeViewing,
  userProfile,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if we have meaningful extracted content (not just empty objects)
  const hasExtractedContent = userProfile && (
    ((userProfile as any).skills && Array.isArray((userProfile as any).skills) && (userProfile as any).skills.length > 0) ||
    ((userProfile as any).experience && Array.isArray((userProfile as any).experience) && (userProfile as any).experience.length > 0) ||
    ((userProfile as any).education && Array.isArray((userProfile as any).education) && (userProfile as any).education.length > 0) ||
    ((userProfile as any).basicDetails && (
      (userProfile as any).basicDetails.name ||
      (userProfile as any).basicDetails.email ||
      (userProfile as any).basicDetails.phone ||
      (userProfile as any).basicDetails.location
    )) ||
    ((userProfile as any).summary && (userProfile as any).summary.trim().length > 0)
  );
  
  const [activeTab, setActiveTab] = useState<string>("pdf");

  // Reset loading state when resume URL changes
  useEffect(() => {
    if (isResumeViewing.resumeUrl) {
      setIsLoading(true);
    }
  }, [isResumeViewing.resumeUrl]);

  const handleCloseResumePreview = () => {
    setIsResumeViewing({
      isResumeViewing: false,
      resumeUrl: null,
    });
    setIsLoading(true);
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const renderExtractedContent = () => {
    if (!userProfile) return null;

    const profile = userProfile as any;
    
    return (
      <div className="resume-preview-extracted-content">
        {profile.basicDetails && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Basic Details</Title>
            {profile.basicDetails.name && (
              <Text strong style={{ display: "block", marginBottom: 4 }}>
                {profile.basicDetails.name}
              </Text>
            )}
            {profile.basicDetails.email && (
              <Text style={{ display: "block", marginBottom: 4 }}>
                Email: {profile.basicDetails.email}
              </Text>
            )}
            {profile.basicDetails.phone && (
              <Text style={{ display: "block", marginBottom: 4 }}>
                Phone: {profile.basicDetails.phone}
              </Text>
            )}
            {profile.basicDetails.location && (
              <Text style={{ display: "block", marginBottom: 4 }}>
                Location: {profile.basicDetails.location}
              </Text>
            )}
          </div>
        )}

        {profile.skills && profile.skills.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Skills</Title>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {profile.skills.map((skill: string, index: number) => (
                <Tag key={index} color="blue">
                  {skill}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {profile.experience && profile.experience.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Experience</Title>
            {profile.experience.map((exp: any, index: number) => (
              <div key={index} style={{ marginBottom: 16, padding: "12px", background: "#f0f2f5", borderRadius: "4px" }}>
                {exp.title && <Text strong>{exp.title}</Text>}
                {exp.company && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    at {exp.company}
                  </Text>
                )}
                {exp.duration && (
                  <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                    {exp.duration}
                  </Text>
                )}
                {exp.description && (
                  <Text style={{ display: "block", marginTop: 8 }}>
                    {exp.description}
                  </Text>
                )}
              </div>
            ))}
          </div>
        )}

        {profile.education && profile.education.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Education</Title>
            {profile.education.map((edu: any, index: number) => (
              <div key={index} style={{ marginBottom: 12 }}>
                {edu.degree && <Text strong>{edu.degree}</Text>}
                {edu.institution && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    - {edu.institution}
                  </Text>
                )}
                {edu.year && (
                  <Text type="secondary" style={{ display: "block" }}>
                    {edu.year}
                  </Text>
                )}
              </div>
            ))}
          </div>
        )}

        {profile.summary && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Summary</Title>
            <Text>{profile.summary}</Text>
          </div>
        )}
      </div>
    );
  };

  const renderPdfContent = () => (
    <div className="resume-preview-pdf-container">
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(8px)",
            zIndex: 1,
            borderRadius: "8px",
            gap: "16px",
          }}
        >
          <div
            style={{
              animation: "aiFloating 3s ease-in-out infinite",
            }}
          >
            <img
              src="/icons/ai-stars.svg"
              alt="Loading"
              style={{
                width: "48px",
                height: "48px",
                filter: "drop-shadow(0 4px 12px rgba(24, 144, 255, 0.3))",
              }}
            />
          </div>
          <Text
            style={{
              color: "rgba(0, 0, 0, 0.65)",
              fontSize: "14px",
              fontWeight: 500,
              animation: "textPulse 2s ease-in-out infinite",
            }}
          >
            Loading resume...
          </Text>
        </div>
      )}
      <iframe
        src={isResumeViewing.resumeUrl || undefined}
        title="Resume Preview"
        onLoad={handleIframeLoad}
      />
    </div>
  );

  const tabItems = useMemo(() => {
    const items = []; 

    // Always add PDF tab if resume URL exists
    if (isResumeViewing.resumeUrl) {
      items.push({
        key: "pdf",
        label: "PDF Preview",
        children: renderPdfContent(),
      });
    }

      // Only add extracted content tab if we have meaningful extracted data
    if (hasExtractedContent) {
      items.push({
        key: "extracted",
        label: "Extracted Content",
        children: renderExtractedContent(),
      });
    }
    
    return items;
  }, [hasExtractedContent, userProfile, isLoading, isResumeViewing.resumeUrl]);

  if (!isResumeViewing.isResumeViewing || !isResumeViewing.resumeUrl) {
    return null;
  }

  return (
    <Drawer
      className="resume-preview-drawer"
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <FileTextOutlined />
          <span>Resume Preview</span>
        </div>
      }
      placement="right"
      onClose={handleCloseResumePreview}
      open={isResumeViewing.isResumeViewing}
      width={750}
      keyboard={false}
    >
      <style>{floatingKeyframes}</style>
      {/* Only show tabs if we have both extracted content AND PDF */}
      {hasExtractedContent && isResumeViewing.resumeUrl && tabItems.length > 1 ? (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="line"
          destroyInactiveTabPane={true}
        />
      ) : hasExtractedContent ? (
        // Show only extracted content if no PDF
        renderExtractedContent()
      ) : isResumeViewing.resumeUrl ? (
        // Show only PDF if no extracted content
        <div className="resume-preview-pdf-container">
          {isLoading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(8px)",
                zIndex: 1,
                borderRadius: "8px",
                gap: "16px",
              }}
            >
              <div
                style={{
                  animation: "aiFloating 3s ease-in-out infinite",
                }}
              >
                <img
                  src="/icons/ai-stars.svg"
                  alt="Loading"
                  style={{
                    width: "48px",
                    height: "48px",
                    filter: "drop-shadow(0 4px 12px rgba(24, 144, 255, 0.3))",
                  }}
                />
              </div>
              <Text
                style={{
                  color: "rgba(0, 0, 0, 0.65)",
                  fontSize: "14px",
                  fontWeight: 500,
                  animation: "textPulse 2s ease-in-out infinite",
                }}
              >
                Loading resume...
              </Text>
            </div>
          )}
          <iframe
            src={isResumeViewing.resumeUrl || undefined}
            title="Resume Preview"
            onLoad={handleIframeLoad}
          />
        </div>
      ) : (
        // Show empty state if neither exists
        <div className="resume-preview-pdf-container">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No data"
          />
        </div>
      )}
    </Drawer>
  );
};

export default ResumePreviewDrawer;
